import { getApiBaseUrl } from '@/config/runtimeUrls';
import { CELL_SCOPE_HEADER, readCellScope } from '@/services/cellScope';

const AUTH_TOKEN_KEYS = [
  'auth_token',
  'auth_v2_accessToken',
  'accessToken',
  'authToken',
  'token',
];

export type MfaMethod = 'email_otp' | 'totp' | 'webauthn';

export type RoleMfaPolicy = {
  enabled: boolean;
  method: MfaMethod;
  trusted_device_days: number;
};

export type MfaPolicy = {
  platform_admin: RoleMfaPolicy;
  operator: RoleMfaPolicy;
  tenant_user: RoleMfaPolicy;
};

const DEFAULT_POLICY: MfaPolicy = {
  platform_admin: { enabled: false, method: 'email_otp', trusted_device_days: 30 },
  operator: { enabled: false, method: 'email_otp', trusted_device_days: 30 },
  tenant_user: { enabled: false, method: 'email_otp', trusted_device_days: 30 },
};

const readAccessToken = (): string => {
  if (typeof window === 'undefined') return '';
  for (const storage of [window.localStorage, window.sessionStorage]) {
    for (const key of AUTH_TOKEN_KEYS) {
      const value = String(storage.getItem(key) || '').trim();
      if (value && value.split('.').length >= 3) return value;
    }
  }
  return '';
};

const withAuthHeaders = (): Record<string, string> => {
  const token = readAccessToken();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    [CELL_SCOPE_HEADER]: readCellScope(),
  };
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  return headers;
};

const normalizeRolePolicy = (candidate: Partial<RoleMfaPolicy> | null | undefined): RoleMfaPolicy => {
  const source = candidate || {};
  const method = String(source.method || 'email_otp').toLowerCase() as MfaMethod;
  const trusted = Number(source.trusted_device_days || 30);
  return {
    enabled: Boolean(source.enabled),
    method: method === 'email_otp' || method === 'totp' || method === 'webauthn' ? method : 'email_otp',
    trusted_device_days: [7, 14, 30, 60].includes(trusted) ? trusted : 30,
  };
};

const normalizePolicy = (candidate: Partial<MfaPolicy> | null | undefined): MfaPolicy => {
  const source = candidate || {};
  return {
    platform_admin: normalizeRolePolicy(source.platform_admin),
    operator: normalizeRolePolicy(source.operator),
    tenant_user: normalizeRolePolicy(source.tenant_user),
  };
};

export const getDefaultMfaPolicy = (): MfaPolicy => JSON.parse(JSON.stringify(DEFAULT_POLICY));

export const fetchMfaPolicy = async (): Promise<MfaPolicy> => {
  const response = await fetch(`${getApiBaseUrl()}/superadmin/settings/mfa-policy`, {
    method: 'GET',
    headers: withAuthHeaders(),
    credentials: 'include',
  });
  if (!response.ok) {
    throw new Error(`Failed to fetch MFA policy (${response.status})`);
  }
  const json = await response.json();
  return normalizePolicy(json?.data?.mfa_policy || DEFAULT_POLICY);
};

export const saveMfaPolicy = async (policy: MfaPolicy): Promise<MfaPolicy> => {
  const response = await fetch(`${getApiBaseUrl()}/superadmin/settings/mfa-policy`, {
    method: 'PUT',
    headers: withAuthHeaders(),
    credentials: 'include',
    body: JSON.stringify({ mfa_policy: normalizePolicy(policy) }),
  });
  if (!response.ok) {
    throw new Error(`Failed to save MFA policy (${response.status})`);
  }
  const json = await response.json();
  return normalizePolicy(json?.data?.mfa_policy || policy);
};
